import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Zap, CheckCircle2, Loader2 } from "lucide-react";

export const FlutterwavePayoutCard = ({ defaultBusinessName = "" }) => {
  const [status, setStatus] = useState(null);
  const [banks, setBanks] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ business_name: "", bank_code: "", account_number: "" });
  const [resolvedName, setResolvedName] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolveUnavailable, setResolveUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [s, b] = await Promise.all([
        api.get("/flutterwave/status"),
        api.get("/flutterwave/banks").catch(() => ({ data: [] })),
      ]);
      setStatus(s.data);
      setBanks(b.data);
      if (!s.data.connected) {
        setForm((f) => ({ ...f, business_name: defaultBusinessName }));
      }
    })();
  }, [defaultBusinessName]);

  const tryResolve = useCallback(async (accountNumber, bankCode) => {
    if (accountNumber.length !== 10 || !bankCode) {
      setResolvedName("");
      return;
    }
    setResolving(true);
    setResolvedName("");
    setResolveUnavailable(false);
    try {
      const { data } = await api.post("/flutterwave/resolve", { account_number: accountNumber, bank_code: bankCode });
      setResolvedName(data.account_name);
    } catch {
      setResolveUnavailable(true);
    } finally {
      setResolving(false);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/flutterwave/connect", form);
      toast.success("Flutterwave connected — buyer payments settle straight to your bank");
      const { data: fresh } = await api.get("/flutterwave/status");
      setStatus(fresh);
      setEditing(false);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const showForm = !status.connected || editing;

  return (
    <div className="mt-12 pt-8 border-t border-white/10" data-testid="flutterwave-payout-section">
      <div className="flex items-center gap-2">
        <Zap className="h-4 w-4 text-[#F5A623]" />
        <h2 className="font-black text-xl tracking-tight">Flutterwave — bank &amp; mobile money</h2>
      </div>
      <p className="mt-2 text-sm text-neutral-400 max-w-lg">
        A second African payout channel. Buyer payments split at charge time and settle{" "}
        <span className="text-white font-semibold">directly to your bank</span> via Flutterwave.
      </p>

      {status.connected && !editing && (
        <div data-testid="flutterwave-connected-card" className="mt-5 rounded-xl border border-[#34C759]/40 bg-[#34C759]/[0.06] p-6">
          <div className="flex items-center gap-2 text-[#34C759]">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-bold text-white">Flutterwave connected</span>
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <Item label="Business" value={status.business_name} />
            <Item label="Bank" value={status.bank_name} />
            <Item label="Account" value={`•••• ${status.account_last4}`} />
            <Item label="Account name" value={status.account_name} />
          </dl>
          <Button
            variant="outline"
            data-testid="flutterwave-update-btn"
            onClick={() => { setEditing(true); setForm({ business_name: status.business_name || "", bank_code: "", account_number: "" }); setResolvedName(""); }}
            className="mt-4 rounded-full bg-transparent border-white/20 text-white hover:bg-white/5"
          >
            Update bank details
          </Button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mt-5 space-y-5">
          <Field label="Business / payout name">
            <Input
              required
              data-testid="flutterwave-business-name"
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="bg-[#121212] border-[#27272A]"
              placeholder="Acme Trading Co."
            />
          </Field>
          <Field label="Bank">
            <select
              required
              data-testid="flutterwave-bank-select"
              value={form.bank_code}
              onChange={(e) => {
                const bank_code = e.target.value;
                setForm({ ...form, bank_code });
                tryResolve(form.account_number, bank_code);
              }}
              className="w-full h-10 rounded-md bg-[#121212] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#F5A623]"
            >
              <option value="">Select your bank…</option>
              {banks.map((b) => (
                <option key={`${b.code}-${b.name}`} value={b.code}>{b.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Account number (10 digits)">
            <Input
              required
              inputMode="numeric"
              maxLength={10}
              data-testid="flutterwave-account-number"
              value={form.account_number}
              onChange={(e) => {
                const account_number = e.target.value.replace(/\D/g, "").slice(0, 10);
                setForm({ ...form, account_number });
                tryResolve(account_number, form.bank_code);
              }}
              className="bg-[#121212] border-[#27272A] font-mono tracking-widest"
              placeholder="0123456789"
            />
          </Field>

          {resolving && (
            <div className="text-xs text-neutral-500 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying account…
            </div>
          )}
          {resolvedName && (
            <div data-testid="flutterwave-resolved-name" className="rounded-md border border-[#34C759]/40 bg-[#34C759]/10 px-3 py-2 text-sm">
              <span className="text-[10px] uppercase tracking-widest text-[#34C759] block">Account verified</span>
              <span className="font-semibold text-white">{resolvedName}</span>
            </div>
          )}
          {resolveUnavailable && !resolvedName && (
            <div data-testid="flutterwave-resolve-unavailable" className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-neutral-400">
              Instant name check unavailable right now — no problem, your bank details will be validated when you connect.
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="flutterwave-connect-btn"
            className="w-full bg-[#F5A623] text-black hover:bg-[#e29a1d] rounded-full h-11 font-semibold"
          >
            <Zap className="h-4 w-4 mr-2" />
            {busy ? "Connecting…" : status.connected ? "Save new bank details" : "Connect Flutterwave"}
          </Button>
          {editing && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setEditing(false)}
              className="w-full text-neutral-400 hover:text-white hover:bg-white/5"
            >
              Cancel
            </Button>
          )}
        </form>
      )}
    </div>
  );
};

function Item({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</dt>
      <dd className="mt-0.5 text-white font-medium">{value || "—"}</dd>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-neutral-400">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
